import { ReduxStoreSlice } from "../../../Redux/ReduxStoreSlice";
import { BookingLocationInfo, BookingWorkFlowState, DefaultBookingWorkFlowState, ReOrderedLocation, locationInitialValue } from "./BookingState";
import { ServiceCheckStatus, PickupServiceCheckState, AccountBookingPayload, PickupServiceabilityDetails } from "../BookingEntities";
import { PaymentOption } from "../../Payment/PaymentEntities";
import { FeatureFlags } from "../../../Config/FeatureFlags";
import { AddressV2 } from "../../../Services/MakeBookingContracts";
import { DateTime } from "luxon";
import { BookingTimeFutureV2, BookingTimeNowV2, BookingTimeV2 } from "../../GbBooking/BookingV2Entities";
import { AddressPointDetails } from "../../../Services/AddressPointsContracts";
import { DeliveryOptionKind } from "../Parcel/ParcelEntities";
import { FullPhoneNumber } from "../../PhoneNumber/PhoneNumberEntities";
import { PerLocationBookingSlice } from "./PerLocationBookingSlice";

const slice = new ReduxStoreSlice<BookingWorkFlowState>("Booking", DefaultBookingWorkFlowState);
const perLocation = new PerLocationBookingSlice(slice);

/** Dispatcher for actions in the Booking state slice. */
export const BookingDispatchV2 = {

    /** Update the address of a booking location */
    Address: perLocation.Action("Address", Address),

    /** Reset (to empty) the address of a booking location. */
    ClearAddress: perLocation.EmptyAction("Clear Address", ClearAddress),

    // timing
    TimeForNow: slice.EmptyAction("Time For Now", TimeForNow),
    TimeForFuture: slice.Action("Time For Future", TimeForFuture),

    /** Update the driver notes for a booking location. */
    DriverNotes: perLocation.Action("Driver Notes", DriverNotes),

    /** Reset / Clear the driver notes for a booking location */
    ClearDriverNotes: perLocation.EmptyAction("Clear Driver Notes", ClearDriverNotes),

    PickupServiceability: slice.Action("Pickup Serviceability", PickupServiceability),
    EnableDiagnosticUI: slice.EmptyAction("Enable Diagnostic UI", HandleEnableDiagnosticUI),
    SetBookOnAccount: slice.Action("Book On Account", HandleSetBookOnAccount),
    PaymentMethod: slice.Action("Change Payment Option", PaymentMethod),
    AccountDetails: slice.Action("Change Account Details", AccountDetails),
    ClearAccountDetails: slice.EmptyAction("Clear Account Details", HandleClearAccountDetails),
    SetDeviceData: slice.Action("Set Device Data", HandleSetDeviceData),
    SetTemplateName: slice.Action("Change Template Name", HandleTemplateNameChange),

    /** Clear / unselect the payment option on the booking */
    ClearPaymentOption: slice.EmptyAction("Clear Payment Option", ClearPaymentOption),

    /** Set the favourite address record used to select the address at this location */
    FavouriteAddress: perLocation.Action("Favourite Address", FavouriteAddress),

    /** Reset / Clear the favourite address linked to the address at this location */
    ClearFavouriteAddress: perLocation.EmptyAction("Clear Favourite Address", ClearFavouriteAddress),

    ChangeDeliveryOption: slice.Action("Change Delivery Option", HandleDeliveryOptionChange),

    /** Reset the Delivery Option to the default (null). */
    ClearDeliveryOption: slice.EmptyAction("Clear Delivery Option", ClearDeliveryOption),
    SetDeliveryOptionError: slice.Action("Set Delivery Option Error", SetDeliveryOptionErrorMessage),

    /** Set the contact or passenger name for a booking location */
    ContactName: perLocation.Action("Contact Name", ContactName),

    /** Clear / reset the contact or passenger name for a booking location */
    ClearContactName: perLocation.EmptyAction("Clear Contact Name", ClearContactName),

    /** Set the phone number for the contact or passenger at a booking location */
    ContactPhone: perLocation.Action("Contact Phone", ContactPhone),

    /** Clear / reset the phone number for the contact or passenger at a booking location */
    ClearContactPhone: perLocation.EmptyAction("Clear Contact Phone", ClearContactPhone),

    /** Generate a fresh ODRD Trip ID after creating a booking. */
    RegenerateOdrdTripId: slice.Action("Regenerate ODRD Trip ID", RegenerateOdrdTripId),

    /** Adds an empty location in to the Locations list in order to create a new destination field in the UI. */
    AddEmptyLocation: slice.EmptyAction("Add Empty Location", AddEmptyLocation),

    /** Removes a location from the Locations list. This is dispatched when the user removes a destination input field. */
    RemoveLocation: slice.Action("Remove Location", RemoveLocation),

    /** Reorder the locations in the Locations list. This is called after dragging and dropping an input field. */
    ReOrderLocations: slice.Action("ReOrder Locations", ReOrderLocations),

    /** When an address is selected from a list of curated locations, update booking store slice with the curated location ID. This value will be used later when creating the booking request/fare estimate request. */
    CuratedLocationAddressId: perLocation.Action("From Curated Location", CuratedLocationAddressId),

    /** When an address is cleared, reset the CuratedLocationAddressId. */
    ResetCuratedLocationAddressId: perLocation.EmptyAction("Clear From Curated Location", ClearCuratedLocationAddressId),

    /** Store the ZoneId from a curated location for zone-based service restrictions. */
    CuratedLocationZoneId: perLocation.Action("Set Curated Location Zone", SetCuratedLocationZoneId),

    /** Clear the ZoneId when address is cleared or changed. */
    ResetCuratedLocationZoneId: perLocation.EmptyAction("Clear Curated Location Zone", ClearCuratedLocationZoneId),

    /** Set the number of seats for Maxi Taxi or SilverServiceExec XL bookings in V2 flow. */
    SetNumberOfSeats: slice.Action("Set Number Of Seats", SetNumberOfSeats),

    /** Clear the number of seats. */
    ClearNumberOfSeats: slice.EmptyAction("Clear Number Of Seats", ClearNumberOfSeats),

    /** Update Silver Service Exec passenger count. */
    UpdateSilverExecPassengers: slice.Action("Update Silver Exec Passengers", UpdateSilverExecPassengers),

    /** Update Silver Service Exec luggage count. */
    UpdateSilverExecLuggage: slice.Action("Update Silver Exec Luggage", UpdateSilverExecLuggage),

    /** Update Silver Service Exec flight number. */
    UpdateSilverExecFlightNumber: slice.Action("Update Silver Exec Flight Number", UpdateSilverExecFlightNumber),

    /** Initialize Silver Service Exec data with defaults. */
    InitializeSilverExec: slice.EmptyAction("Initialize Silver Exec", InitializeSilverExec),

    /** Clear Silver Service Exec data. */
    ClearSilverExec: slice.EmptyAction("Clear Silver Exec", ClearSilverExec),

    /** Update Silver Service Exec XL passenger count. */
    UpdateSilverExecXlPassengers: slice.Action("Update Silver Exec XL Passengers", UpdateSilverExecXlPassengers),

    /** Update Silver Service Exec XL luggage count. */
    UpdateSilverExecXlLuggage: slice.Action("Update Silver Exec XL Luggage", UpdateSilverExecXlLuggage),

    /** Update Silver Service Exec XL flight number. */
    UpdateSilverExecXlFlightNumber: slice.Action("Update Silver Exec XL Flight Number", UpdateSilverExecXlFlightNumber),

    /** Initialize Silver Service Exec XL data with defaults. */
    InitializeSilverExecXl: slice.EmptyAction("Initialize Silver Exec XL", InitializeSilverExecXl),

    /** Clear Silver Service Exec XL data. */
    ClearSilverExecXl: slice.EmptyAction("Clear Silver Exec XL", ClearSilverExecXl),

    /** Set Silver Service Exec validation error message */
    SetSilverExecError: slice.Action("Set Silver Exec Error", SetSilverExecError),

    /** Clear Silver Service Exec validation error */
    ClearSilverExecError: slice.EmptyAction("Clear Silver Exec Error", ClearSilverExecError),
};

/** Reducer for the Booking store slice */
export const BookingReducerV2 = slice.MakeCombinedReducer();

/** Apply a booking time update action to the booking state (book for now). */
function TimeForNow(state: BookingWorkFlowState): BookingWorkFlowState {

    const timeV2: BookingTimeNowV2 = {
        IsImmediate: true
    };

    return BookingTimeHandler(state, timeV2);
}

/** Apply a booking time update action to the booking state (book for future). */
function TimeForFuture(state: BookingWorkFlowState, bookingTime: DateTime): BookingWorkFlowState {

    const timeV2: BookingTimeFutureV2 = {
        RequestedDate: bookingTime,
        IsImmediate: false
    };

    return BookingTimeHandler(state, timeV2);
}

/** Generate a shared BookingWorkFlowState for update time now and future. */
function BookingTimeHandler(state: BookingWorkFlowState, timeV2: BookingTimeV2): BookingWorkFlowState {

    return {
        ...state,
        BookingTimeV2: timeV2
    };
}

/** Handler for the PickupServiceCheckUpdate action. This will update the PickupServiceCheck data. */
function PickupServiceability(bookingState: BookingWorkFlowState, serviceabilityDetails: PickupServiceabilityDetails): BookingWorkFlowState {

    // invalid updates get ignored
    if (!IsProposedStateChangeValid(bookingState, serviceabilityDetails.PickupPlaceId, serviceabilityDetails.ServiceabilityCheckState)) {
        return bookingState;
    }

    return {
        ...bookingState,
        PickupServiceCheck: serviceabilityDetails.ServiceabilityCheckState,
        PickupPlaceId: serviceabilityDetails.PickupPlaceId,
    };
}

/** This is turning into a mini reducer for CreateBookingPayload.PickupServiceCheck. Should i be using recompose or something?? */
function IsProposedStateChangeValid(currentBookingState: BookingWorkFlowState, pickupAddressPlaceId: string, proposedNewState: PickupServiceCheckState): boolean {

    // check is only fitting to BookingController
    if (FeatureFlags.GlobalBooker) return true;

    // updates to indeterminate state are always OK
    if ((proposedNewState.status === ServiceCheckStatus.CheckInProgress) || (proposedNewState.status === ServiceCheckStatus.NoInputSelected)) {
        return true;
    }

    // result update: only honour if it still matches the pickup address
    const existingPickup = currentBookingState.Locations[0].Address;
    return !!existingPickup && existingPickup.GoogleMapsPlaceId === pickupAddressPlaceId;
}

/** Handle a EnableDiagnosticUI action. Just sets the flag to true. */
function HandleEnableDiagnosticUI(state: BookingWorkFlowState): BookingWorkFlowState {
    return {
        ...state,
        ShowDiagnosticUI: true,
    };
}

/** Enable/disable Book on account feature based on the given value */
function HandleSetBookOnAccount(state: BookingWorkFlowState, newValue: boolean): BookingWorkFlowState {
    return {
        ...state,
        IsOnAccount: newValue
    };
}

/** Handle the Payment option change for Booking Payload */
function PaymentMethod(state: BookingWorkFlowState, selectedPaymentOption: PaymentOption): BookingWorkFlowState {
    return {
        ...state,
        PaymentOption: selectedPaymentOption
    };
};

/** Handle updating account details for bookings on account. */
function AccountDetails(state: BookingWorkFlowState, accountDetails: AccountBookingPayload): BookingWorkFlowState {
    return {
        ...state,
        AccountData: accountDetails,
    };
}

/** Handle clear account details when Book on account is disabled. */
function HandleClearAccountDetails(state: BookingWorkFlowState): BookingWorkFlowState {
    return {
        ...state,
        AccountData: null
    };
}

/** Handle for setting device data for Booking Payload */
function HandleSetDeviceData(state: BookingWorkFlowState, deviceData: string): BookingWorkFlowState {
    return {
        ...state,
        DeviceData: deviceData
    };
};

/** Handle the Template name */
function HandleTemplateNameChange(state: BookingWorkFlowState, name: string): BookingWorkFlowState {
    return {
        ...state,
        TemplateName: name,
    };
}

/** Clear / unselect the payment option on the booking */
function ClearPaymentOption(state: BookingWorkFlowState): BookingWorkFlowState {

    const { PaymentOption, ...newCreatePayload } = state;

    return newCreatePayload;
};

/** Handle the delivery option change for Booking Payload */
function HandleDeliveryOptionChange(state: BookingWorkFlowState, selectedDeliveryOptionValue: DeliveryOptionKind): BookingWorkFlowState {
    return {
        ...state,
        DeliveryOption: selectedDeliveryOptionValue
    };
};

/** Reset the Delivery Option to the default (null). */
function ClearDeliveryOption(state: BookingWorkFlowState): BookingWorkFlowState {
    return {
        ...state,
        DeliveryOption: null
    };
}

/** Set the error message if delivery option not selected when new parcel booking */
function SetDeliveryOptionErrorMessage(state: BookingWorkFlowState, errorMessage: string | null): BookingWorkFlowState {
    return {
        ...state,
        DeliveryOptionError: errorMessage
    }
}

function RegenerateOdrdTripId(state: BookingWorkFlowState, newId: string): BookingWorkFlowState {
    return {
        ...state,
        GoogleOdrdTripId: newId,
    };
}

// #region Per-location reducer definition

/** Update the address of a booking location */
function Address(state: BookingLocationInfo, address: AddressV2): BookingLocationInfo {
    return {
        ...state,
        Address: address,
    };
}

/** Reset (to empty) the address of a booking location. */
function ClearAddress(state: BookingLocationInfo): BookingLocationInfo {
    return {
        ...state,
        Address: null,
    };
}

/** Update the driver notes for a booking location. */
function DriverNotes(state: BookingLocationInfo, notes: string): BookingLocationInfo {
    return {
        ...state,
        DriverNotes: notes,
    };
}

/** Reset / Clear the driver notes for a booking location */
function ClearDriverNotes(state: BookingLocationInfo): BookingLocationInfo {
    return {
        ...state,
        DriverNotes: null,
    };
}

/** Set the favourite address record used to select the address at this location */
function FavouriteAddress(state: BookingLocationInfo, favourite: AddressPointDetails): BookingLocationInfo {
    return {
        ...state,
        Favourite: favourite,
    };
}

/** Reset / Clear the favourite address linked to the address at this location */
function ClearFavouriteAddress(state: BookingLocationInfo): BookingLocationInfo {
    return {
        ...state,
        Favourite: null,
    };
}

/** Set the contact or passenger name for a booking location */
function ContactName(state: BookingLocationInfo, contactName: string): BookingLocationInfo {
    return {
        ...state,
        Contact: {
            ...state.Contact,
            Name: contactName,
        }
    };
}

/** Clear / reset the contact or passenger name for a booking location */
function ClearContactName(state: BookingLocationInfo): BookingLocationInfo {

    const { Name, ...otherContactDetails } = state.Contact;

    return {
        ...state,
        Contact: otherContactDetails,
    };
}

/** Set the phone number for the contact or passenger at a booking location */
function ContactPhone(state: BookingLocationInfo, contactPhone: FullPhoneNumber): BookingLocationInfo {
    return {
        ...state,
        Contact: {
            ...state.Contact,
            Phone: contactPhone
        }
    }
}

/** Clear / reset the phone number for the contact or passenger at a booking location */
function ClearContactPhone(state: BookingLocationInfo): BookingLocationInfo {
    return {
        ...state,
        Contact: {
            ...state.Contact,
            Phone: null
        }
    }
}

// #endregion

/** Adds an empty location in to the Locations list in order to create a new destination field in the UI. */
function AddEmptyLocation(state: BookingWorkFlowState): BookingWorkFlowState {

    return {
        ...state,
        Locations: [...state.Locations, locationInitialValue]
    };
}

/** Removes a location from the Locations list. */
function RemoveLocation(state: BookingWorkFlowState, index: number): BookingWorkFlowState {
    return {
        ...state,
        Locations: state.Locations.filter((_, i) => i !== index)
    };
}

/** Reorder the locations in the Locations list. */
function ReOrderLocations(state: BookingWorkFlowState, location: ReOrderedLocation): BookingWorkFlowState {

    const newLocations = state.Locations;
    const currentIndex = newLocations.indexOf(location.Location);

    newLocations.splice(currentIndex, 1);
    newLocations.splice(location.NewIndex, 0, location.Location);

    return {
        ...state,
        Locations: newLocations
    }
}

/** When an address is selected from a list of curated locations, update booking store slice with the curated location ID. This value will be used later when creating the booking request/fare estimate request. */
function CuratedLocationAddressId(state: BookingLocationInfo, curatedLocationId: number): BookingLocationInfo {
    return {
        ...state,
        CuratedLocationAddressId: curatedLocationId,
    };
}

/** When an address is cleared, reset the CuratedLocationAddressId. */
function ClearCuratedLocationAddressId(state: BookingLocationInfo): BookingLocationInfo {
    return {
        ...state,
        CuratedLocationAddressId: null,
    }
}

/** Store the ZoneId from a curated location for zone-based service restrictions. */
function SetCuratedLocationZoneId(state: BookingLocationInfo, zoneId: number): BookingLocationInfo {
    return {
        ...state,
        CuratedLocationZoneId: zoneId,
    };
}

/** Clear the ZoneId when address is cleared or changed. */
function ClearCuratedLocationZoneId(state: BookingLocationInfo): BookingLocationInfo {
    return {
        ...state,
        CuratedLocationZoneId: null,
    };
}

/** Set the number of seats for Maxi Taxi or SilverServiceExec XL bookings. */
function SetNumberOfSeats(state: BookingWorkFlowState, numberOfSeats: number): BookingWorkFlowState {
    return {
        ...state,
        NumberOfSeats: numberOfSeats,
    };
}

/** Clear the number of seats. */
function ClearNumberOfSeats(state: BookingWorkFlowState): BookingWorkFlowState {
    return {
        ...state,
        NumberOfSeats: null,
    };
}

// Silver Service Exec Reducers

/** Update passenger count for Silver Service Exec. NumberOfSeats equals passengers. */
function UpdateSilverExecPassengers(state: BookingWorkFlowState, passengers: number): BookingWorkFlowState {
    const current = state.SilverExec || { Passengers: 1, Luggage: 0, FlightNumber: "" };
    return {
        ...state,
        SilverExec: { ...current, Passengers: passengers },
        NumberOfSeats: passengers,
    };
}

/** Update luggage count for Silver Service Exec. */
function UpdateSilverExecLuggage(state: BookingWorkFlowState, luggage: number): BookingWorkFlowState {
    const current = state.SilverExec || { Passengers: 1, Luggage: 0, FlightNumber: "" };
    return {
        ...state,
        SilverExec: { ...current, Luggage: luggage },
    };
}

/** Update flight number for Silver Service Exec. */
function UpdateSilverExecFlightNumber(state: BookingWorkFlowState, flightNumber: string): BookingWorkFlowState {
    const current = state.SilverExec || { Passengers: 1, Luggage: 0, FlightNumber: "" };
    return {
        ...state,
        SilverExec: { ...current, FlightNumber: flightNumber },
    };
}

/** Initialize Silver Service Exec with default values. */
function InitializeSilverExec(state: BookingWorkFlowState): BookingWorkFlowState {
    return {
        ...state,
        SilverExec: { Passengers: 1, Luggage: 0, FlightNumber: "" },
        NumberOfSeats: 1,
    };
}

/** Clear Silver Service Exec data. */
function ClearSilverExec(state: BookingWorkFlowState): BookingWorkFlowState {
    return {
        ...state,
        SilverExec: null,
    };
}

// Silver Service Exec XL Reducers

/** Update passenger count for Silver Service Exec XL. NumberOfSeats equals passengers. */
function UpdateSilverExecXlPassengers(state: BookingWorkFlowState, passengers: number): BookingWorkFlowState {
    const current = state.SilverExecXl || { Passengers: 5, Luggage: 0, FlightNumber: "" };
    return {
        ...state,
        SilverExecXl: { ...current, Passengers: passengers },
        NumberOfSeats: passengers,
    };
}

/** Update luggage count for Silver Service Exec XL. */
function UpdateSilverExecXlLuggage(state: BookingWorkFlowState, luggage: number): BookingWorkFlowState {
    const current = state.SilverExecXl || { Passengers: 5, Luggage: 0, FlightNumber: "" };
    return {
        ...state,
        SilverExecXl: { ...current, Luggage: luggage },
    };
}

/** Update flight number for Silver Service Exec XL. */
function UpdateSilverExecXlFlightNumber(state: BookingWorkFlowState, flightNumber: string): BookingWorkFlowState {
    const current = state.SilverExecXl || { Passengers: 5, Luggage: 0, FlightNumber: "" };
    return {
        ...state,
        SilverExecXl: { ...current, FlightNumber: flightNumber },
    };
}

/** Initialize Silver Service Exec XL with default values (5 passengers). */
function InitializeSilverExecXl(state: BookingWorkFlowState): BookingWorkFlowState {
    return {
        ...state,
        SilverExecXl: { Passengers: 5, Luggage: 0, FlightNumber: "" },
        NumberOfSeats: 5,
    };
}

/** Clear Silver Service Exec XL data. */
function ClearSilverExecXl(state: BookingWorkFlowState): BookingWorkFlowState {
    return {
        ...state,
        SilverExecXl: null,
    };
}

/** Set Silver Service Exec validation error message */
function SetSilverExecError(state: BookingWorkFlowState, errorMessage: string): BookingWorkFlowState {
    return {
        ...state,
        SilverExecError: errorMessage,
    };
}

/** Clear Silver Service Exec validation error */
function ClearSilverExecError(state: BookingWorkFlowState): BookingWorkFlowState {
    return {
        ...state,
        SilverExecError: null,
    };
}
